import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GetReferenceDetailDocument,
  SubmitReferenceFileJobDocument,
  type SubmitReferenceFileJobMutation,
  type SubmitReferenceFileJobMutationVariables,
} from "~/features/references/gqls";
import type { ReferenceFileAddFormData } from "~/features/references/types";
import { extractGoogleDriveFileId } from "~/features/references/utils/reference";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Add / replace a reference file by submitting an async job that pulls the file
 * from Google Drive and generates page images. Accepts a raw id or a Drive URL.
 */
export function useReferenceFileAdd({ referenceId, onSuccess }: { referenceId: string; onSuccess?: () => void }) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [submitJob] = useMutation<SubmitReferenceFileJobMutation, SubmitReferenceFileJobMutationVariables>(
    SubmitReferenceFileJobDocument,
  );

  const submit = useCallback(
    async (form: ReferenceFileAddFormData) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        await submitJob({
          refetchQueries: [{ query: GetReferenceDetailDocument, variables: { tenantId, id: referenceId } }],
          variables: {
            input: { tenantId, referenceId, gdriveFileId: extractGoogleDriveFileId(form.googleDriveFileId) },
          },
        });
        toast.success(t("feature.common.createdSuccessfully", { model: "referenceFile" }));
        onSuccess?.();
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [submitJob, tenantId, referenceId, t, onSuccess],
  );

  return { submit, processing };
}
